import { useEffect, useState } from "react";
import { GripHorizontalIcon, ImageOffIcon, PlayIcon } from "lucide-react";

import { Column, Row, Text } from "@/components";

import { media } from "@/lib/media";
import { ui } from "@/lib/ui";
import { MediaLink, MediaLinkType } from "@/types";

const MIN_HEIGHT = 120;
const DEFAULT_HEIGHT = 160;

export const ReadOnlyMediaGallery = ({
  mediaLinks,
}: {
  mediaLinks: MediaLink[];
}) => {
  /* State */
  const [containerHeight, setContainerHeight] = useState(DEFAULT_HEIGHT);
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);

  /* Handlers */
  const handleMouseDown = ui.createVerticalDragHandler({
    minValue: MIN_HEIGHT,
    currentValue: containerHeight,
    onDrag: setContainerHeight,
  });

  /* Derived */
  const itemHeight = containerHeight - 16; // padding
  const itemWidth = Math.round(itemHeight * 1.33); // 4:3 aspect ratio

  /* Render */
  const render = () => {
    /* Render — empty state */
    if (mediaLinks.length === 0) {
      return null;
    }

    return (
      <Column className="gap-0">
        {/* Scrollable media container */}
        <div
          className="flex gap-3 overflow-x-auto overflow-y-hidden px-1 py-2"
          style={{ height: containerHeight }}
        >
          {mediaLinks.map((media) => {
            return (
              <div
                key={media.id}
                className="flex-shrink-0 overflow-hidden rounded-lg border border-slate-200"
                style={{ width: itemWidth, height: itemHeight }}
              >
                <MediaItem
                  media={media}
                  playingVideoId={playingVideoId}
                  setPlayingVideoId={setPlayingVideoId}
                />
              </div>
            );
          })}
        </div>

        {/* Drag handle */}
        <div
          className="flex h-4 cursor-row-resize items-center justify-center border-b border-slate-200"
          onMouseDown={handleMouseDown}
        >
          <GripHorizontalIcon className="h-5 w-5 text-slate-300" />
        </div>
      </Column>
    );
  };

  return render();
};

const MediaItem = ({
  media,
  playingVideoId,
  setPlayingVideoId,
}: {
  media: MediaLink;
  playingVideoId: string | null;
  setPlayingVideoId: (videoId: string | null) => void;
}) => {
  const render = () => {
    // Display image
    if (media.type === MediaLinkType.UPLOADED_IMAGE) {
      return <ImageThumbnail url={media.url ?? ""} />;
    }

    // If it's not an image, it's a video. If it's the currently playing video, display the embedded youtube video.
    if (playingVideoId === media.id) {
      return <YouTubeEmbed url={media.url ?? ""} />;
    }

    // Otherwise, display the video thumbnail.
    return (
      <VideoThumbnail
        onClick={() => {
          setPlayingVideoId(media.id);
        }}
        url={media.url ?? ""}
      />
    );
  };

  return render();
};

const ImageThumbnail = ({ url }: { url: string }) => {
  const [error, setError] = useState(false);
  const [prevUrl, setPrevUrl] = useState(url);

  // Refresh the image if the URL changes - needed for unsigned -> signed URL transition.
  useEffect(() => {
    if (url !== prevUrl) {
      setPrevUrl(url);
      setError(false);
    }
  }, [url, prevUrl]);

  const render = () => {
    if (!url || error) {
      return (
        <Column className="h-full w-full items-center justify-center gap-1 bg-slate-50">
          <ImageOffIcon className="h-5 w-5 text-slate-400" />
          <Text className="text-[10px]" variant="muted">
            Failed to load
          </Text>
        </Column>
      );
    }
    return (
      <img
        alt="Photo"
        className="h-full w-full cursor-pointer object-contain"
        onClick={() => window.open(url, "_blank")}
        onError={() => {
          setError(true);
        }}
        src={url}
      />
    );
  };

  return render();
};

const VideoThumbnail = ({
  url,
  onClick,
}: {
  url: string;
  onClick: () => void;
}) => {
  const thumbnailSrc = media.createYouTubeThumbnailLink(url);

  return (
    <div className="relative h-full w-full cursor-pointer" onClick={onClick}>
      {thumbnailSrc ? (
        <img
          alt="Video thumbnail"
          className="h-full w-full object-cover"
          src={thumbnailSrc}
        />
      ) : (
        <Column className="h-full w-full items-center justify-center bg-slate-100">
          <ImageOffIcon className="h-5 w-5 text-slate-400" />
        </Column>
      )}
      <Row className="absolute inset-0 items-center justify-center bg-black/20 transition-colors hover:bg-black/30">
        <Row className="h-10 w-10 items-center justify-center rounded-full bg-red-600 shadow-md">
          <PlayIcon className="ml-0.5 h-5 w-5 fill-white text-white" />
        </Row>
      </Row>
    </div>
  );
};

const YouTubeEmbed = ({ url }: { url: string }) => {
  const videoId = media.extractYouTubeVideoId(url);

  const render = () => {
    if (!videoId) {
      return (
        <Column className="h-full w-full items-center justify-center bg-slate-100">
          <Text className="text-xs" variant="muted">
            Unable to play video
          </Text>
        </Column>
      );
    }

    return (
      <iframe
        allow="autoplay; clipboard-write; encrypted-media;"
        allowFullScreen
        className="h-full w-full"
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
        title="YouTube video"
      />
    );
  };

  return render();
};
